import { useEffect, useRef, useState } from 'react';
import type { CreateDraftRequest, CreateDraftResponse } from '@erp/rpc';
import { useCreateDraft, useListWorkflowDrafts } from './useWorkflowVersioning';
import { withAsyncCleanup } from '@/lib/withAsyncCleanup';
import { useGT } from 'gt-react';
import { useToast } from '@/context/ToastContext';

type DraftEntryParams = CreateDraftRequest & { sourceVersionNumber?: number };
export type DraftEntryDecision = 'continue' | 'live' | 'requested' | 'cancel';
type PendingEntry = {
  params: DraftEntryParams;
  result: CreateDraftResponse;
  resolve: (result: CreateDraftResponse | null) => void;
};

function draftReplacementRequest(
  entry: PendingEntry,
  decision: 'live' | 'requested',
): CreateDraftRequest {
  return {
    workflowId: entry.params.workflowId,
    sourceVersionId:
      decision === 'live'
        ? (entry.result.freshness?.pinnedVersionId ?? undefined)
        : entry.params.sourceVersionId,
    onExistingDraft: 'replace',
  };
}

// A decision made just before navigation applies once to the mounted editor.
// Bookmarks and subsequent visits must still consult the server.
const approvedEntries = new Set<string>();
export function consumeDraftEntryDecision(draftId: string) {
  return approvedEntries.delete(draftId);
}

export function useWorkflowDraftManager() {
  const gt = useGT();
  const { showToast } = useToast();
  const { mutateAsync: createDraft } = useCreateDraft();
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const requestDraft = async (input: CreateDraftRequest) => {
    setIsCreatingDraft(true);
    return withAsyncCleanup(
      () => createDraft(input),
      () => setIsCreatingDraft(false),
    );
  };
  const { data: draftsData } = useListWorkflowDrafts({
    ownedByCurrentUser: true,
  });
  const [pendingEntry, setPendingEntry] = useState<PendingEntry | null>(null);
  const pendingRef = useRef<PendingEntry | null>(null);
  const entryPromiseRef = useRef<{
    params: DraftEntryParams;
    promise: Promise<CreateDraftResponse | null>;
  } | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pendingRef.current?.resolve(null);
    };
  }, []);

  const ensureDraft = (params: DraftEntryParams) => {
    if (entryPromiseRef.current) {
      const active = entryPromiseRef.current;
      const sameRequest =
        active.params.workflowId === params.workflowId &&
        active.params.sourceVersionId === params.sourceVersionId &&
        active.params.onExistingDraft === params.onExistingDraft;
      return sameRequest ? active.promise : Promise.resolve(null);
    }
    const enter = async () => {
      const { sourceVersionNumber: _sourceVersionNumber, ...input } = params;
      const result = await requestDraft(input);
      if (!mountedRef.current) return null;
      if (
        input.onExistingDraft === 'replace' ||
        !(
          result.requestedSourceIgnored ||
          (result.resumed && result.freshness?.suggestRestart)
        )
      ) {
        approvedEntries.add(result.definition.id);
        return result;
      }
      return await new Promise<CreateDraftResponse | null>((resolve) => {
        const entry = { params, result, resolve };
        pendingRef.current = entry;
        setPendingEntry(entry);
      });
    };
    const promise = enter().finally(() => {
      entryPromiseRef.current = null;
    });
    entryPromiseRef.current = { params, promise };
    return promise;
  };

  const chooseDraft = async (decision: DraftEntryDecision) => {
    const entry = pendingRef.current;
    if (!entry) return;
    let result: CreateDraftResponse | null = null;
    return withAsyncCleanup(
      async () => {
        try {
          if (decision === 'continue') result = entry.result;
          if (decision === 'live' || decision === 'requested') {
            result = await requestDraft(
              draftReplacementRequest(entry, decision),
            );
          }
          if (result) approvedEntries.add(result.definition.id);
        } catch (error) {
          showToast({
            title: gt('Failed to create draft', {
              context:
                'toast title when replacing an existing workflow draft fails',
            }),
            message: error instanceof Error ? error.message : String(error),
            color: 'error',
          });
        }
      },
      () => {
        pendingRef.current = null;
        setPendingEntry(null);
        entry.resolve(result);
      },
    );
  };

  const findExistingDraft = (workflowId: string) =>
    draftsData?.drafts.find((draft) => draft.workflowId === workflowId);

  return {
    ensureDraft,
    findExistingDraft,
    pendingEntry,
    chooseDraft,
    isCreatingDraft,
  };
}
